/**
 * Applies smoothing to each cell in a table using the average of surrounding cells weighted by a kernel
 * @param {number[][]} table - 2D array of values
 * @param {number[][]} kernel - 2D square array of odd numbered width
 * @returns {number[][]} smoothed - the smoothed table
 */
export const smooth = (table, kernel=null) => {
    if(!kernel){
        kernel = [
            [1, 2, 1],
            [2, 4, 2],
            [1, 2, 1]
        ];
    }

    // apply to each cell
    return table.map((row, i) => {
        return row.map((cell, j) => {
            const extraction = weightedExtract(table, kernel, { i, j });
            return mean(extraction, kernel);
        });
    });
};

/**
 * Extracts an area the size of the kernel centred around a point from the table. Returns the extraction weighted
 * by kernel values
 * @param {number[][]} table - 2D array of values
 * @param {number[][]} kernel - 2D square array of odd numbered width
 * @param {Point} point - the centre point in the table to evaluate
 * @returns {(number|null)[][]} extraction - weighted extraction
 */
const weightedExtract = (table, kernel, point) => {
    const { i, j } = point;
    const paddingI = (kernel.length - 1) / 2;
    const paddingJ = (kernel[0].length - 1) / 2;

    return kernel.map((row, y) => {
        return row.map((cell, x) => {
            const yOffset = y + i - paddingI;
            const xOffset = x + j - paddingJ;
            return (table[yOffset] != null && table[yOffset][xOffset] != null) ? (table[yOffset][xOffset] * cell) : null;
        });
    });
};

/**
 * Calculates the mean of all non-null values in a 2D array, accounting for kernel weighting
 * @param {(number|null)[][]} extraction - a weighted extraction produced by the kernel
 * @param {number[][]} kernel - a kernel
 * @returns {number} mean - the average value
 */
const mean = (extraction, kernel) => {
    // synchronise null values from extraction into kernel
    let nullKernel = kernel.map((row, y) => {
        return row.map((cell, x) => {
            return (extraction[y] != null && extraction[y][x] != null) ? cell : null;
        })
    });

    //calculate sum skipping undefined or null values
    const sum = (accumulator, current) => typeof current === "number" ? accumulator + current : accumulator;
    const total = extraction.reduce((acc, val) => acc.concat(val), []).reduce(sum);
    const weight = nullKernel.reduce((acc, val) => acc.concat(val), []).reduce(sum);

    return total === 0 ? 0 : total / weight;
};

export const testables = { weightedExtract, mean };